import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IdentificationTypeEnum } from '../types/user.enum.type';
import { Icons } from 'components/Icon';
import IdentificationCard from 'components/Cards/IdentificationCard';
import TextField from 'components/Form/TextField';
import Button from 'components/Form/Button';
import { IdentityType } from '../types/on-boarding.type';
import { useIdentityVerificationUpdate } from 'common/queries-and-mutations/user';
import { findNextPage, findPreviousPage } from 'utils';
import { selectAccountDetails } from 'selectors/account-selector';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { STEPS } from 'constants/steps';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';
import verification from '../assets/verification.png';

const NIN_LENGTH = 11;

// A saved National ID with a NIN number came from the NIN card
const toCardIdentificationType = (type: string, identificationId: string) =>
  type === IdentificationTypeEnum.NATIONAL_ID && identificationId
    ? IdentificationTypeEnum.NIN
    : type;

export default function IdentificationType() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError } = useIdentityVerificationUpdate();
  const { user } = useAppSelector(selectAccountDetails);
  const navigate = useNavigate();
  const planAllowPage = user && JSON.parse(user?.planAllowPage);
  const dispatch = useAppDispatch();
  const savedType = toCardIdentificationType(
    user.identificationType,
    user.identificationId
  );
  const savedNin =
    savedType === IdentificationTypeEnum.NIN ? user.identificationId : '';
  const [showCompleted, setShowCompleted] = useState(Boolean(savedNin));
  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const formik = useFormik({
    initialValues: {
      identificationType: IdentificationTypeEnum.NIN as string,
      identificationId: savedNin || '',
      identificationBase64: '',
      base64FileName: ''
    },
    validationSchema: Yup.object({
      identificationId: Yup.string()
        .required('NIN is required')
        .matches(
          new RegExp(`^\\d{${NIN_LENGTH}}$`),
          `NIN must be ${NIN_LENGTH} digits`
        )
    }),
    enableReinitialize: true,
    onSubmit: values => {
      setIsSubmitting(true);
      onIdentitySubmit(values);
    }
  });

  const onIdentitySubmit = (formValue: IdentityType) => {
    if (formValue.identificationId === savedNin) {
      navigate(findNextPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
      return;
    }
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [dispatch, isError, isSuccess, navigate, planAllowPage]);

  const onContinueLater = async () => {
    formik.setFieldTouched('identificationId', true, false);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      return;
    }
    setIsLater(true);
    mutate(formik.values);
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.INDENTITY_VERIFICATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={3}
        stepPercentage={STEPS.INDENTITY_VERIFICATION}
        timelineStep={1}
      />
      <div className="flex flex-col items-center justify-center">
        <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
          National Identification Number (NIN)
        </p>

        <form onSubmit={formik.handleSubmit} className="mx-10 w-full">
          {showCompleted ? (
            <div className="mt-10 flex items-center justify-center space-x-4">
              <div className="h-32 w-32 overflow-hidden rounded-full">
                <img
                  src={verification}
                  alt="Your Image Alt Text"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col items-center">
                <p className="rounded-lg bg-gray-200 p-4">
                  Your Identity has been submitted for review
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Modify"
                  type="button"
                  onClick={() => setShowCompleted(false)}
                ></Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="mt-5 flex flex-wrap justify-center gap-5">
                <IdentificationCard
                  onSelect={() => {}}
                  selectedIdentity={formik.values.identificationType}
                  fieldValue={IdentificationTypeEnum.NIN}
                  fieldTitle="NIN"
                  fieldIcon={Icons.BVN}
                />
              </div>
              <div className="mx-auto max-w-md p-8">
                <TextField
                  name="identificationId"
                  placeholder=""
                  maxLength={NIN_LENGTH}
                  value={formik.values.identificationId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue(
                      'identificationId',
                      e.target.value.replace(/\D/g, '')
                    )
                  }
                  onBlur={formik.handleBlur}
                  label="Enter your NIN"
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                  error={
                    formik.touched.identificationId
                      ? formik.errors.identificationId
                      : undefined
                  }
                />
              </div>
            </div>
          )}
          <div className="flex w-full items-end justify-between">
            <Button
              variant="outline"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
              label="Previous"
              type="button"
              onClick={() => onPrevious()}
            ></Button>
            <div className="mt-10 flex items-end justify-end gap-8">
              {parentPath !== 'dashboard' && (
                <button
                  type="button"
                  className="font-circular text-[15px] font-bold text-[#353535] underline"
                  onClick={onContinueLater}
                >
                  Save and Continue Later
                </button>
              )}
              <Button
                variant="primary"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem]"
                label="Proceed"
                type="submit"
                disabled={!formik.values.identificationId}
                loading={isSubmitting}
              ></Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
